import { Grid, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { DbUser } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import axios from "../../../../services/axios";
import { AlertState } from "../../../../types";
import { TechnicalIssuesMessage } from "../../utils/Toast";
import { AxiosError } from "axios";
import {
  SchoolCreationState,
  getError,
  initSchoolCreationState,
  useSchoolCreationState,
} from "../state/useSchoolCreationState";
import { School } from "@backend-types";
import { DistrictSearch } from "../utils/DistrictSearch";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import { generateGtmEvent } from "../../../../Helpers";

export type AddSchoolProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

type SchoolRequest = Pick<School, "name" | "district">;

const useStyle = makeStyles((theme) => ({
  gridItem: {
    padding: "0px !important",
  },
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));

export function AddSchool(props: AddSchoolProps) {
  const [state, dispatch] = useSchoolCreationState(props);
  const classes = useStyle();
  const [, setDistrictName] = useState<string | null>(null);
  const [, setSchoolName] = useState<string | null>(null);
  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string,
    metaData?: any
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
  };

  const updateForm = useCallback(
    (update: Partial<SchoolCreationState["form"]>) =>
      dispatch({ type: "updateForm", update }),
    [dispatch]
  );

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (schoolName && schoolName?.length > 0) {
  //       HandleDataLayer(
  //         "form",
  //         "click",
  //         "onclick",
  //         "blur",
  //         "modal",
  //         "add school ^ school",
  //         schoolName
  //       );
  //     }
  //   }, 1000);
  //   return () => clearTimeout(timeout);
  // }, [schoolName]);

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (districtName && districtName?.length > 0) {
  //       HandleDataLayer(
  //         "form",
  //         "click",
  //         "onclick",
  //         "blur",
  //         "modal",
  //         "create school ^ district",
  //         districtName
  //       );
  //     }
  //   }, 1000);
  //   return () => clearTimeout(timeout);
  // }, [districtName]);

  const submit = async () => {
    HandleDataLayer("form", "click", "onclick", "click", "modal", "add school", "save");
    const { form } = state;
    const { name: schoolName } = form;

    try {
      await axios.post<SchoolRequest>("/schools", {
        name: schoolName,
        district: form.district,
      });
      generateGtmEvent(
        {
          event: "form",
          action: "creating a school",
          name: "onsubmit",
          type: "submit",
          region: "main content",
          section: "create school",
          text: "school created",
        },
        { school: `${schoolName}`, district: `${form.district}` }
      );
      props.onSubmit({
        severity: "success",
        message: `Created ${schoolName}`,
      });
      dispatch({
        type: "replaceState",
        state: initSchoolCreationState(props.currentUser),
      });
    } catch (e: any) {
      if (
        e instanceof AxiosError &&
        typeof e.response?.data === "string" &&
        e.response?.data.includes("DuplicateSchoolNameException")
      ) {
        props.onSubmit({
          severity: "error",
          message: `${schoolName} already exists. Please use another name.`,
        });
      } else {
        props.onSubmit({
          severity: "error",
          message: TechnicalIssuesMessage,
        });
      }
      throw e;
    }
  };

  const handleClose = () => {
    // generateGtmEvent({
    //   event: "modal",
    //   action: "close",
    //   name: "onclick",
    //   type: "click",
    //   region: "main content",
    //   section: "add school",
    //   text: "close cross",
    // });
    props.onClose();
  };

  return (
    <UserManagementModal
      title="Add school"
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      errorText={getError(state)}>
      <Typography color="textSecondary" sx={{ marginBottom: "2rem" }}>
        Please enter the school name and district.
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            label="School"
            value={state.form.name}
            onChange={(e) => {
              generateGtmEvent({
                event: "form",
                action: "click",
                name: "onclick",
                type: "blur",
                region: "main content",
                section: "create school ^ school",
                text: `${e.target.value}`,
              });
              updateForm({ name: e.target.value });
              setSchoolName(e.target.value);
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <DistrictSearch
            selected={state.form.district}
            options={state.data.districts.value}
            onSelect={(e) => {
              updateForm({ district: e.target.value });
              setDistrictName(e.target.value);
            }}
            onChange={(e) => {
              generateGtmEvent({
                event: "form",
                action: "click",
                name: "onclick",
                type: "blur",
                region: "main content",
                section: "create school ^ district",
                text: `${e.target.value}`,
              });
              updateForm({ district: e.target.value });
              setDistrictName(e.target.value);
            }}
            onAutoCompleteChange={(e, value) => {
              generateGtmEvent({
                event: "select",
                action: "click",
                name: "onclick",
                type: "select a district",
                region: "main content",
                section: "create school ^ district",
                text: `${value}`,
              });
              updateForm({ district: value ?? "" });
            }}
          />
        </Grid>
      </Grid>
    </UserManagementModal>
  );
}
